import {
  RelationToStudentSchema,
  YesOrNo,
} from '@/apis/family_application/requests.ts'
import { EducationPlansnOption } from '@/apis/family_application/student-details.requests.ts'
import { updateStudent } from '@/app-storage/applications/upsert-student.action.ts'
import { selectProgramID } from '@/app-storage/program_settings/programSettingsSlice.ts'
import { AppDispatch, RootState } from '@/app-storage/store'
import { useAppAlert } from '@/app/AppAlertProvider.tsx'
import { DatePicker } from '@/app/common/components/DatePicker.tsx'
import { Input } from '@/app/common/components/Input.tsx'
import { isV1RenewalStudent } from '@/app/utils/student/student.utils.ts'
import { BASE_DATE_FORMAT } from '@/common/constants.ts'
import { FamilyApplicationQueryKeys } from '@/queries/family_applications/queries.ts'
import { AppRoutePaths } from '@/routing/route_paths'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { differenceInYears, format, Locale, parse, subYears } from 'date-fns'
import { enUS, es } from 'date-fns/locale'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import FamilyWizardLayout from '../FamilyWizardLayout.tsx'
import useStudenDetailsDocumentParams from './hooks/useStudentDetailsDocumentParams.tsx'
import { SubmitButton } from './shared/submit-button.tsx'

const RELATIONSHIP_OPTIONS = [
  { label: 'parent', value: 'parent' },
  { label: 'adoptive_parent', value: 'adoptive_parent' },
  { label: 'legal_guardian', value: 'legal_guardian' },
  { label: 'other', value: 'other' },
] as const

const EDUCATION_PLAN_OPTIONS: {
  value: number
  label: string
}[] = [
  { value: 1, label: 'part_public_district' },
  { value: 2, label: 'part_public_charter' },
  { value: 3, label: 'micro_school' },
  { value: 4, label: 'private_school' },
  { value: 5, label: 'full_time_home_school' },
  { value: 6, label: 'other' },
] as const

const studentSchema = z
  .object({
    first_name: z.string().min(1, 'first_name_is_required'),
    last_name: z.string().min(1, 'last_name_is_required'),
    middle_name: z.string().nullable().optional(),
    relation_to_student: RelationToStudentSchema,
    date_of_birth: z
      .date()
      .refine(
        (date) => isEligibleStudent(date),
        'Student must be atleast 5 years old'
      ),
    SSID: z.string().max(15).nullable().optional(),
    force: z.boolean().optional(),
    education_plan: z.string().min(1, 'answer_is_required'),
    require_special_education: z.string().optional(),
  })
  .refine(
    (value) => {
      if (value.date_of_birth) {
        const age = getAge(value.date_of_birth)

        return age > 18 && age <= 21
          ? Boolean(value.require_special_education)
          : true
      }
      return true
    },
    {
      message: 'special_education_answer_is_required',
      path: ['require_special_education'],
    }
  )

type FormValues = z.infer<typeof studentSchema>

function isEligibleStudent(birthDate: Date) {
  return getAge(birthDate) >= 5
}

function getCutoffDate() {
  const currentYear = new Date().getFullYear()
  const cutoffDate = new Date(currentYear, 8, 2) // September 2, 2024
  return cutoffDate
}

function getAge(birthDate: Date) {
  // Eligibility cutoff date
  const cutoffDate = getCutoffDate()
  // Make sure the student is at least 5 years old
  const age = differenceInYears(cutoffDate, birthDate)

  return age
}

const options: {
  value: number
  label: string
}[] = [
  { value: 0, label: 'No' },
  { value: 1, label: 'Yes' },
] as const

export const StudentDetailsSubPage = () => {
  const ProgramId = useSelector(selectProgramID)
  const [open, setIsOpen] = useState(false)
  const [isPosibleSpecialEducation, setPosibleSpecialEducation] =
    useState(false)
  const { t: translate, i18n } = useTranslation()
  const { onStudentDetailFormSelect } = useStudenDetailsDocumentParams()
  const dispatch = useDispatch<AppDispatch>()
  const { fireAlert } = useAppAlert()
  const navigateTo = useNavigate()
  const queryClient = useQueryClient()
  const {
    selectedStudentId,
    data: FamilyApplication,
    request: { status: updateStudentStatus },
  } = useSelector((state: RootState) => state.FamilyApplication)
  const programSettings = useSelector(
    (state: RootState) => state.ProgramSettings
  )
  const student = FamilyApplication?.students?.find(
    (student) => student.id === selectedStudentId
  )
  const currentLanguage = i18n.language

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(studentSchema),
    defaultValues: {
      first_name: student?.first_name ?? '',
      last_name: student?.last_name ?? '',
      middle_name: student?.middle_name ?? '',
      date_of_birth: student?.date_of_birth
        ? parse(student.date_of_birth, BASE_DATE_FORMAT, new Date())
        : undefined,
      relation_to_student: student?.relationship ?? undefined,
      SSID: student?.SSID,
      education_plan: student?.education_plan
        ? String(student.education_plan)
        : '',
      require_special_education:
        student?.require_special_education?.toString() ?? undefined,
    },
  })

  useEffect(() => {
    if (control._formValues.date_of_birth) {
      const age = getAge(control._formValues.date_of_birth)

      if (age > 18 && age <= 21) {
        setPosibleSpecialEducation(true)
      } else {
        setPosibleSpecialEducation(false)
      }
    }
  }, [control._formValues.date_of_birth])

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const formattedDate = format(data.date_of_birth, BASE_DATE_FORMAT)
    const resultAction = await dispatch(
      updateStudent({
        first_name: data.first_name,
        last_name: data.last_name,
        middle_name: data.middle_name ?? undefined,
        student_id: selectedStudentId ?? undefined,
        relationship: data.relation_to_student,
        program_id: ProgramId,
        date_of_birth: formattedDate,
        SSID: data.SSID ?? undefined,
        force: data.force,
        education_plan: Number(data.education_plan) as EducationPlansnOption,
        require_special_education: Number(
          data.require_special_education
        ) as YesOrNo,
      })
    )

    if (updateStudent.fulfilled.match(resultAction)) {
      queryClient
        .invalidateQueries({
          queryKey: FamilyApplicationQueryKeys.attestations_text,
        })
        .then()
      const { student_details: studentDetailsPaths } =
        AppRoutePaths.family_application


      if (!isPosibleSpecialEducation) {
        if (data.relation_to_student !== "parent") {
          onStudentDetailFormSelect('relation_to_student')
          return
        }
        navigateTo(studentDetailsPaths.capture_instructions)
      } else {
        if (data.require_special_education === '1') {
          onStudentDetailFormSelect('form_other')
        } else {
          fireAlert({
            variant: 'error',
            message: translate('student_is_too_old'),
          })
        }
      }
    } else {
      fireAlert({
        variant: 'error',
        denyBtn: true,
        denyBtnText: translate('cancel'),
        confirmBtnText: translate('confirm'),
        message:
          resultAction.payload?.message ?? translate('student_already_exists'),
      }).then((result) => {
        const STUDENT_ALREADY_EXISTS_ERROR_CODE = 17
        const isUpdateStudentError =
          resultAction?.payload?.code === STUDENT_ALREADY_EXISTS_ERROR_CODE

        if (result.confirmed && isUpdateStudentError) {
          setValue('force', true)
          handleSubmit(onSubmit)()
        }
      })
    }
  }

  function getLocale(string: string): Locale {
    if (string === 'es') {
      return es
    }

    return enUS
  }

  const handleSSIDChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatSSN(e.target.value)
    setValue('SSID', formattedValue)
  }

  const formatSSN = (ssn: string) => {
    // remove all non-dash and non-numerals
    const val = ssn.replace(/[^\d-]/g, '')

    // enforce max length
    return val.substring(0, 15)
  }

  function isRenewal() {
    if (!student) {
      return false
    }

    return isV1RenewalStudent(student)
  }

  function getMaxAge() {
    const cutoffDate = getCutoffDate()

    const minDate = subYears(cutoffDate, 21)

    return minDate
  }

  const isIneligibleExternalStudent = student?.is_external_student && student?.application_status === "Ineligible"

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('student_details')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content>
        <p
          id="form-title"
          className="text-xl font-medium text-gray-900 mb-3 sm:mb-2"
        >
          {translate('basic_information')}
        </p>
        <p id="form-desc" className="text-sm font-small text-gray-500">
          {translate('applying_student_text')}
        </p>
        <form
          id="student-details-form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3"
          aria-labelledby="form-title"
          aria-describedby="form-desc"
        >
          <div className="my-10">
            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="first_name_label"
              aria-describedby="first_name_desc"
            >
              <label
                id="first_name_label"
                htmlFor="first_name"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('legal_first_name')}{' '}
                <span className="text-danger ml-1">*</span>
              </label>
              <div>
                <Input
                  disabled={isRenewal()}
                  id="first_name"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  {...register('first_name')}
                />
              </div>
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.first_name.message ?? '')}
                </p>
              )}
              <div
                id="first_name_desc"
                className="mt-1 rounded-md px-2 py-2 text-xs font-medium ring-1 ring-inset bg-primary-50 ring-primary-600/20"
              >
                {isRenewal() ? (
                  <>Name cannot be changed for renewal student</>
                ) : (
                  translate('name_entered_must_match_with_legal_name')
                )}
              </div>
            </section>

            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="middle_name_label"
            >
              <label
                id="middle_name_label"
                htmlFor="middle_name"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('identity_fields.legal_middle_name')}
              </label>
              <div>
                <Input
                  disabled={isRenewal()}
                  id="middle_name"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  {...register('middle_name')}
                />
              </div>
              {errors.middle_name && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.middle_name.message ?? '')}
                </p>
              )}
            </section>

            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="last_name_label"
              aria-describedby="last_name_desc"
            >
              <label
                id="last_name_label"
                htmlFor="last_name"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('legal_last_name')}{' '}
                <span className="text-danger ml-1">*</span>
              </label>
              <div>
                <Input
                  disabled={isRenewal()}
                  id="last_name"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  {...register('last_name')}
                />
              </div>
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.last_name.message ?? '')}
                </p>
              )}
              <div
                id="last_name_desc"
                className="mt-1 rounded-md px-2 py-2 text-xs font-medium ring-1 ring-inset bg-primary-50 ring-primary-600/20"
              >
                {isRenewal() ? (
                  <>Last name cannot be changed for renewal student</>
                ) : (
                  translate('name_entered_must_match_with_legal_name')
                )}
              </div>
            </section>

            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="date_of_birth_label"
            >
              <label
                id="date_of_birth_label"
                htmlFor="date_of_birth"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('student_birth_date')}
                <span className="text-danger ml-1">*</span>
              </label>

              <Controller
                name="date_of_birth"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    fromDate={getMaxAge()}
                    toDate={new Date()}
                    captionLayout="dropdown"
                    open={open}
                    onOpenChange={setIsOpen}
                    locale={getLocale(currentLanguage)}
                    className="w-full"
                    disabled={isIneligibleExternalStudent}
                    disableNavigation={isIneligibleExternalStudent}
                    selected={field.value}
                    defaultMonth={field.value}
                    mode="single"
                    onSelect={(date) => {
                      field.onChange(date)
                      if (date) {
                        setIsOpen(false)
                      }
                    }}
                  />
                )}
              />

              {errors.date_of_birth && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.date_of_birth.message ?? '')}
                </p>
              )}
            </section>

            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="ssid_label"
            >
              <label
                id="ssid_label"
                htmlFor="SSID"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('know_ssid')}
              </label>
              <div>
                <Input
                  id="SSID"
                  {...register('SSID')}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  onChange={handleSSIDChange}
                />
              </div>
              {errors.SSID && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.SSID.message ?? '')}
                </p>
              )}
              <div
                id="relation_to_student_desc"
                className="inline-flex items-center rounded-md px-2 py-2 text-xs font-medium ring-1 ring-inset bg-primary-50 ring-primary-600/20"
              >
                {translate('ssid_stands')}
              </div>
            </section>

            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="relation_to_student_label"
              aria-describedby="relation_to_student_desc"
            >
              <label
                id="relation_to_student_label"
                htmlFor="relation_to_student"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('relationship_to_student')}:
                <span className="text-danger ml-1">*</span>
              </label>

              <div>
                <select
                  id="relation_to_student"
                  {...register('relation_to_student')}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  defaultValue=""
                >
                  <option value="" disabled hidden>
                    {translate('select_relation_to_student')}
                  </option>
                  {RELATIONSHIP_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {translate(option.label)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col mt-6 space-y-2">
                {errors.relation_to_student && (
                  <p className="mt-1 text-sm text-red-600">
                    {translate(errors.relation_to_student.message ?? '')}
                  </p>
                )}
              </div>

              <div
                id="relation_to_student_desc"
                className="inline-flex items-center rounded-md px-2 py-2 text-xs font-medium ring-1 ring-inset bg-primary-50 ring-primary-600/20"
              >
                {translate('select_other')}
              </div>
            </section>

            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="education_plan_label"
            >
              <label
                id="education_plan_label"
                htmlFor="education_plan"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('education_plan')}:
                <span className="text-danger ml-1">*</span>
              </label>

              <select
                id="education_plan"
                {...register('education_plan')}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                defaultValue=""
              >
                <option value="" disabled hidden>
                  {translate('please_select_an_option')}
                </option>

                {EDUCATION_PLAN_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {translate(option.label)}
                  </option>
                ))}
              </select>
              <div
                id="require_special_education_desc"
                className="mt-1 rounded-md px-2 py-2 text-xs font-medium ring-1 ring-inset bg-primary-50 ring-primary-600/20"
              >
                {translate('plan_to_educate_student_help', {
                  program_start_date: programSettings.data?.program_start_date
                    ? format(
                      new Date(programSettings.data.program_start_date),
                      'yyyy'
                    )
                    : '',
                  program_end_date: programSettings.data?.program_end_date
                    ? format(
                      new Date(programSettings.data.program_end_date),
                      'yyyy'
                    )
                    : '',
                })}
              </div>

              <div className="flex flex-col mt-6 space-y-2">
                {errors.education_plan && (
                  <p className="mt-1 text-sm text-red-600">
                    {translate(errors.education_plan?.message ?? '')}
                  </p>
                )}
              </div>
            </section>

            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="require_special_education_label"
              aria-describedby="require_special_education_desc"
            >
              <label
                id="require_special_education_label"
                htmlFor="require_special_education"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('require_special_education')}:
                {isPosibleSpecialEducation && (
                  <span className="text-danger ml-1">*</span>
                )}
              </label>

              <select
                id="require_special_education"
                {...register('require_special_education')}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                defaultValue=""
              >
                <option value="" disabled hidden>
                  {translate('please_select_an_option')}
                </option>

                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {translate(option.label.toLocaleLowerCase())}
                  </option>
                ))}
              </select>

              <div className="flex flex-col mt-6 space-y-2">
                {errors.require_special_education && (
                  <p className="mt-1 text-sm text-red-600">
                    {translate(errors.require_special_education?.message ?? '')}
                  </p>
                )}
              </div>

              <div
                id="require_special_education_desc"
                className="mt-1 rounded-md px-2 py-2 text-xs font-medium ring-1 ring-inset bg-primary-50 ring-primary-600/20"
              >
                {translate('documentation_required_older_student')}
              </div>
            </section>
          </div>
        </form>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        {' '}
        <SubmitButton
          form="student-details-form"
          status={updateStudentStatus}
        />
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
